//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import {
  IInstagramStoryGroupAnalyticsData,
  InstagramStorySearchSortKey,
} from '@apis/gateway-instagram/instagramStoryAnalyticsApiModel';
import Cuid from '@models/Cuid';
import DateString from '@models/DateString';
import { IInstagramStoryClip } from '@models/IInstagramMediaItem';
import { UniversalSearchSortDirection } from '@models/UniversalSearchSortDirection';
import { makeParams } from '@utils/makeParams';
import { mapObject } from '@utils/mapObject';
import { serializedGatewayFetch } from '@utils/sessionFetch';

function storyAnalyticsFetch(endpoint: string, init?: RequestInit) {
  return serializedGatewayFetch(
    'instagram_dashboard_gateway',
    `/api/v1/instagram/analytics/stories${endpoint}`,
    init,
  );
}

interface IStoryClipsAnalyticsResponse {
  data: {
    storyClipsByCuid: Record<string, IInstagramStoryClip>;
  };
}

/**
 * Retrieve the client's Instagram Story Clips data for the given clip Cuids.
 *
 * /api/v1/instagram/analytics/stories/clips
 */
export async function getStoryClipsAnalytics(
  cuids: Cuid[],
): Promise<Record<Cuid, IInstagramStoryClip>> {
  const req = await storyAnalyticsFetch('/clips', {
    method: 'POST',
    body: JSON.stringify({
      cuids,
    }),
  });

  const res = await req.json<IStoryClipsAnalyticsResponse>((d) => ({
    data: {
      storyClipsByCuid: mapObject(d.data.storyClipsByCuid, (c) => ({
        ...c,
        postedDateTime: new Date(c.postedDateTime),
      })),
    },
  }));
  return res.data.storyClipsByCuid;
}

interface IStoryGroupAnalyticsResponse {
  data: IInstagramStoryGroupAnalyticsData;
}

/**
 * Retrieve the client's Instagram Story Groups engagement data for the given date range.
 *
 * /api/v1/instagram/analytics/stories/groups
 */
export async function getStoryGroupsAnalytics(options: {
  startDate: DateString;
  endDate: DateString;
  sortKey?: InstagramStorySearchSortKey;
  sortDirection?: UniversalSearchSortDirection;
}): Promise<IInstagramStoryGroupAnalyticsData> {
  const params = makeParams(options);
  const req = await storyAnalyticsFetch(`/groups?${params}`);
  const res = await req.json<IStoryGroupAnalyticsResponse>((d) => ({
    data: {
      ...d.data,
      storyGroups: d.data.storyGroups.map((g) => ({
        ...g,
        startTime: new Date(g.startTime),
        endTime: new Date(g.endTime),
      })),
      swipeUpLinkData: d.data.swipeUpLinkData.map((s) => ({
        ...s,
        scheduledTimestamp: new Date(s.scheduledTimestamp),
      })),
      swipeUpLinkDataByLocalDate: Object.fromEntries(
        Object.entries(d.data.swipeUpLinkDataByLocalDate).map((entry) => {
          const [key, value] = entry;
          return [
            key,
            value.map((val) => {
              return {
                ...val,
                scheduledTimestamp: new Date(val.scheduledTimestamp),
              };
            }),
          ];
        }),
      ),
    },
  }));
  return res.data;
}

/**
 * Retrieve the client's Instagram Story Groups aggregate stats for the given date range.
 *
 * /api/v1/instagram/analytics/stories/groups/download/groups-and-clips.zip
 */
export async function getStoryGroupsDataDownload(options: {
  startDate: DateString;
  endDate: DateString;
  sortKey?: InstagramStorySearchSortKey;
  sortDirection?: UniversalSearchSortDirection;
}) {
  const params = makeParams(options);
  const req = await storyAnalyticsFetch(
    `/groups/download/groups-and-clips.zip?${params}`,
  );
  return req.blob();
}
